import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Divider,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Toolbar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Public as RegionIcon,
  Storage as ClusterIcon,
  DateRange as DateRangeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import esdlService from '../../services/esdlService';
import OpenSearchClusterHealth from './OpenSearchClusterHealth';
import OpenSearchStats from './OpenSearchStats';
import OpenSearchNodeCard from './OpenSearchNodeCard';
import OpenSearchMetricsChart from './OpenSearchMetricsChart';

/**
 * OpenSearch Monitoring Dashboard Component
 */
const OpenSearchDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for data
  const [loading, setLoading] = useState({
    overview: false,
    health: false,
    nodes: false,
    metrics: false
  });
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // State for OpenSearch data
  const [overviewData, setOverviewData] = useState(null);
  const [clusterHealth, setClusterHealth] = useState(null);
  const [nodeMetrics, setNodeMetrics] = useState([]);
  const [metricsData, setMetricsData] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    region: '',
    cluster: '',
    timeRange: '1h'
  });
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    regions: [],
    clusters: [],
    timeRanges: [
      { value: '10m', label: 'Last 10 Minutes' },
      { value: '30m', label: 'Last 30 Minutes' },
      { value: '1h', label: 'Last Hour' },
      { value: '6h', label: 'Last 6 Hours' },
      { value: '24h', label: 'Last 24 Hours' },
      { value: '7d', label: 'Last 7 Days' }
    ]
  });
  
  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      // Get regions
      const regionsResponse = await esdlService.getRegions();
      // Make sure regions is an array of strings, not objects
      const regions = Array.isArray(regionsResponse?.regions) 
        ? regionsResponse.regions.map(region => {
            // If region is an object with a name property, use that
            if (typeof region === 'object' && region !== null) {
              return region.name || region.region || String(region);
            }
            // Otherwise, convert to string
            return String(region);
          })
        : [];
      
      // Get clusters
      const clustersResponse = await esdlService.getOpenSearchOverview();
      // Make sure we handle the clusters data properly
      const clusters = Array.isArray(clustersResponse?.clusters) 
        ? clustersResponse.clusters.map(c => c.cluster_name || 'Unknown')
        : [];
      
      setFilterOptions(prev => ({
        ...prev,
        regions,
        clusters
      }));
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setError('Failed to load filter options');
    }
  }, []);
  
  // Fetch overview data
  const fetchOverviewData = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, overview: true }));
      
      // Make sure region is a string
      const regionParam = filters.region && typeof filters.region === 'object' 
        ? (filters.region.name || filters.region.region || String(filters.region))
        : filters.region;
        
      const response = await esdlService.getOpenSearchOverview({
        region: regionParam
      });
      setOverviewData(response);
      setLoading(prev => ({ ...prev, overview: false }));
    } catch (error) {
      console.error('Error fetching OpenSearch overview:', error);
      setError('Failed to load OpenSearch overview data');
      setLoading(prev => ({ ...prev, overview: false }));
    }
  }, [filters.region]);
  
  // Fetch cluster health
  const fetchClusterHealth = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, health: true }));
      
      // Make sure region is a string
      const regionParam = filters.region && typeof filters.region === 'object' 
        ? (filters.region.name || filters.region.region || String(filters.region))
        : filters.region;
      
      const response = await esdlService.getOpenSearchHealth({
        region: regionParam,
        cluster_name: filters.cluster
      });
      
      // Make sure we're handling the cluster health data properly
      const sanitizedHealth = response ? { ...response } : null;
      setClusterHealth(sanitizedHealth);
      setLoading(prev => ({ ...prev, health: false }));
    } catch (error) {
      console.error('Error fetching OpenSearch health:', error);
      setError('Failed to load OpenSearch health data');
      setLoading(prev => ({ ...prev, health: false }));
    }
  }, [filters.region, filters.cluster]);
  
  // Fetch node metrics
  const fetchNodeMetrics = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, nodes: true }));
      
      // Make sure region is a string
      const regionParam = filters.region && typeof filters.region === 'object' 
        ? (filters.region.name || filters.region.region || String(filters.region))
        : filters.region;
      
      const response = await esdlService.getOpenSearchNodeMetrics({
        region: regionParam,
        cluster_name: filters.cluster
      });
      // Make sure we're handling the nodes array properly
      const nodes = Array.isArray(response?.nodes) ? response.nodes : [];
      setNodeMetrics(nodes);
      setLoading(prev => ({ ...prev, nodes: false }));
    } catch (error) {
      console.error('Error fetching OpenSearch node metrics:', error);
      setError('Failed to load OpenSearch node metrics');
      setLoading(prev => ({ ...prev, nodes: false }));
    }
  }, [filters.region, filters.cluster]);
  
  // Fetch metrics data
  const fetchMetricsData = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, metrics: true }));
      
      // Make sure region is a string
      const regionParam = filters.region && typeof filters.region === 'object' 
        ? (filters.region.name || filters.region.region || String(filters.region))
        : filters.region;
      
      const response = await esdlService.getOpenSearchTimeseries({
        region: regionParam,
        cluster_name: filters.cluster,
        range: filters.timeRange
      });
      // Make sure we're handling the metrics data properly
      const sanitizedResponse = response ? { ...response } : null;
      setMetricsData(sanitizedResponse);
      setLoading(prev => ({ ...prev, metrics: false }));
    } catch (error) {
      console.error('Error fetching OpenSearch metrics:', error);
      setError('Failed to load OpenSearch metrics data');
      setLoading(prev => ({ ...prev, metrics: false }));
    }
  }, [filters.region, filters.cluster, filters.timeRange]);
  
  // Fetch all data
  const fetchAllData = useCallback(() => {
    fetchOverviewData();
    fetchClusterHealth();
    fetchNodeMetrics();
    fetchMetricsData();
  }, [fetchOverviewData, fetchClusterHealth, fetchNodeMetrics, fetchMetricsData]);
  
  // Initial data load
  useEffect(() => {
    fetchFilterOptions();
    fetchAllData();
  }, [fetchFilterOptions, fetchAllData]);
  
  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    
    // Make sure we're handling the value correctly, especially for regions
    const processedValue = typeof value === 'object' && value !== null 
      ? (value.name || value.region || String(value)) 
      : value;
    
    setFilters(prev => ({ ...prev, [name]: processedValue }));
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle refresh
  const handleRefresh = () => {
    fetchAllData();
  };
  
  // Prepare CPU chart data
  const prepareCpuChartData = () => {
    if (!metricsData || !metricsData.cpu || !metricsData.cpu.timestamps || !metricsData.cpu.values) {
      console.warn('No CPU metrics data available:', metricsData);
      return null;
    }
    
    console.log('Preparing CPU chart data with:', {
      timestamps: metricsData.cpu.timestamps.length,
      values: metricsData.cpu.values.length
    });
    
    return {
      labels: metricsData.cpu.timestamps.map(ts => new Date(ts).toLocaleTimeString()),
      datasets: [
        {
          label: 'CPU Load (1 min)',
          data: metricsData.cpu.values,
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          fill: true
        }
      ]
    };
  };
  
  // Prepare Memory chart data
  const prepareMemoryChartData = () => {
    if (!metricsData || !metricsData.memory || !metricsData.memory.timestamps || !metricsData.memory.values) {
      console.warn('No Memory metrics data available:', metricsData);
      return null;
    }
    
    console.log('Preparing Memory chart data with:', {
      timestamps: metricsData.memory.timestamps.length,
      values: metricsData.memory.values.length
    });
    
    return {
      labels: metricsData.memory.timestamps.map(ts => new Date(ts).toLocaleTimeString()),
      datasets: [
        {
          label: 'Memory Usage (%)',
          data: metricsData.memory.values,
          borderColor: theme.palette.secondary.main,
          backgroundColor: alpha(theme.palette.secondary.main, 0.1),
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          fill: true
        }
      ]
    };
  };
  
  // Prepare JVM Heap chart data
  const prepareJvmHeapChartData = () => {
    if (!metricsData || !metricsData.heap || !metricsData.heap.timestamps || !metricsData.heap.values) {
      console.warn('No JVM Heap metrics data available:', metricsData);
      return null;
    }
    
    console.log('Preparing JVM Heap chart data with:', {
      timestamps: metricsData.heap.timestamps.length,
      values: metricsData.heap.values.length
    });
    
    return {
      labels: metricsData.heap.timestamps.map(ts => new Date(ts).toLocaleTimeString()),
      datasets: [
        {
          label: 'JVM Heap Usage (%)',
          data: metricsData.heap.values,
          borderColor: theme.palette.error.main,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          fill: true
        }
      ]
    };
  };
  
  // Prepare chart data for search rate
  const prepareSearchRateChartData = () => {
    if (!metricsData || !metricsData.search_rate || !Array.isArray(metricsData.search_rate.timestamps) || !Array.isArray(metricsData.search_rate.values)) {
      return null;
    }
    
    return {
      labels: metricsData.search_rate.timestamps.map(ts => {
        const date = new Date(ts);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'Search Rate (queries/sec)',
          data: metricsData.search_rate.values,
          borderColor: theme.palette.warning.main,
          backgroundColor: alpha(theme.palette.warning.main, 0.2),
          fill: true,
          tension: 0.4
        }
      ]
    };
  };
  
  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          fontWeight="bold" 
          sx={{ 
            mt: 2,
            mb: 3,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}
        >
          OpenSearch Monitoring
        </Typography>
        
        {/* Filter Bar */}
        <Paper
          elevation={0}
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="region-select-label">Region</InputLabel>
                  <Select
                    labelId="region-select-label"
                    id="region-select"
                    name="region"
                    value={filters.region}
                    onChange={handleFilterChange}
                    label="Region"
                    startAdornment={
                      <RegionIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                    }
                  >
                    <MenuItem value="">All Regions</MenuItem>
                    {filterOptions.regions.map((region, index) => (
                      <MenuItem key={index} value={typeof region === 'string' ? region : String(region)}>
                        {typeof region === 'string' ? region : String(region)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="cluster-select-label">Cluster</InputLabel>
                  <Select
                    labelId="cluster-select-label"
                    id="cluster-select"
                    name="cluster"
                    value={filters.cluster}
                    onChange={handleFilterChange}
                    label="Cluster"
                    startAdornment={
                      <ClusterIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                    }
                  >
                    <MenuItem value="">All Clusters</MenuItem>
                    {filterOptions.clusters.map((cluster, index) => (
                      <MenuItem key={index} value={cluster}>{cluster}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="time-range-select-label">Time Range</InputLabel>
                  <Select
                    labelId="time-range-select-label"
                    id="time-range-select"
                    name="timeRange"
                    value={filters.timeRange}
                    onChange={handleFilterChange}
                    label="Time Range"
                    startAdornment={
                      <DateRangeIcon sx={{ ml: 1, mr: 0.5, color: 'action.active', fontSize: '1.2rem' }} />
                    }
                  >
                    {filterOptions.timeRanges.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ 
                    textTransform: 'none',
                    borderRadius: 2,
                    py: 1
                  }}
                >
                  Refresh
                </Button>
                <Tooltip title="Settings">
                  <IconButton sx={{ ml: 1 }}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Toolbar>
        </Paper>
        
        {/* OpenSearch Stats */}
        <OpenSearchStats 
          data={overviewData ? {
            ...overviewData,
            // Make sure clusters is not rendered directly
            clusters: typeof overviewData.clusters_count === 'number' 
              ? overviewData.clusters_count 
              : (Array.isArray(overviewData.clusters) ? overviewData.clusters.length : 0),
            // Ensure all values are primitive types
            regions: Array.isArray(overviewData.regions) 
              ? overviewData.regions.length 
              : (typeof overviewData.regions === 'number' ? overviewData.regions : 0),
            // Remove any potentially problematic objects
            clusters_details: undefined
          } : null} 
          loading={loading.overview}
          onRefresh={fetchOverviewData}
        />
        
        <Divider sx={{ my: 4, opacity: 0.6 }} />
        
        {/* Cluster Health */}
        <Typography variant="h6" fontWeight="medium" mb={3}>Cluster Health</Typography>
        <OpenSearchClusterHealth 
          clusterHealth={clusterHealth} 
          loading={loading.health}
          onRefresh={fetchClusterHealth}
        />
        
        <Divider sx={{ my: 4, opacity: 0.6 }} />
        
        {/* Metrics Charts */}
        <Typography variant="h6" fontWeight="medium" mb={3}>Performance Metrics</Typography>
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <OpenSearchMetricsChart
              title="CPU Usage"
              subtitle="Average CPU usage across cluster nodes"
              chartType="line"
              chartData={prepareCpuChartData()}
              loading={loading.metrics}
              onRefresh={fetchMetricsData}
              height={280}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OpenSearchMetricsChart
              title="Memory Usage"
              subtitle="Average memory usage across cluster nodes"
              chartType="line"
              chartData={prepareMemoryChartData()}
              loading={loading.metrics}
              onRefresh={fetchMetricsData}
              height={280}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OpenSearchMetricsChart
              title="JVM Heap Usage"
              subtitle="Average JVM heap usage across cluster nodes"
              chartType="line"
              chartData={prepareJvmHeapChartData()}
              loading={loading.metrics}
              onRefresh={fetchMetricsData}
              height={280}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OpenSearchMetricsChart
              title="Search Rate"
              subtitle="Search queries per second"
              chartType="line"
              chartData={prepareSearchRateChartData()}
              loading={loading.metrics}
              onRefresh={fetchMetricsData}
              height={280}
            />
          </Grid>
        </Grid>
        
        {/* Node Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="medium">Cluster Nodes</Typography>
            <Button 
              variant="outlined" 
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => {}}
              sx={{ textTransform: 'none' }}
            >
              Filter
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {loading.nodes ? (
              Array(3).fill(0).map((_, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      height: 300, 
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      backdropFilter: 'blur(8px)',
                    }}
                  />
                </Grid>
              ))
            ) : nodeMetrics.length > 0 ? (
              nodeMetrics.map((node, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <OpenSearchNodeCard 
                    node={node}
                    onViewDetails={() => {}}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    py: 5,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    borderRadius: 3
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No node data available
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchNodeMetrics}
                    sx={{ mt: 2, textTransform: 'none' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default OpenSearchDashboard; 